'use client'
 
import { useState } from 'react'
import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, MoreHorizontal, Globe, Phone, Mail, MapPin, Calendar, Trash2, User, UserCircle, Building, Clock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { EditUserForm } from "./EditUserForm"
import FormModal from "@/components/FormModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Edit2, Loader2 } from "lucide-react"
import { Badge } from '@/components/ui/badge'
import UserService from '@/api/users.service'
import { useToast } from '@/hooks/use-toast'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

// This type is used to define the shape of our data.
export type User = {
  id: number;
  publicId: string;
  username: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  gender: string;
  city: string;
  physicalAddress: string;
  country: string;
  password: string;
  isAdmin: boolean;
  isActive: boolean;
  status: string;
  activationCode: string;
  timezone: string;
  dateUpdated: string;
  dateCreated: string;
}

export const columns: ColumnDef<User>[] = [
  {
    header: "Actions",
    cell: ({ row }) => {
      const [isModalOpen, setModalOpen] = useState(false);
      const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
      const [isRemoveModalOpen, setRemoveModalOpen] = useState(false);
      const [isRemoving, setIsRemoving] = useState(false);
      const [isDeleting, setIsDeleting] = useState(false);
      const { toast } = useToast();

      const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
        toast({
          title: title,
          description: description,
          variant: toastType === "error" ? "destructive" : "default",
          duration: duration,
        });
      };

      const [selectedBusiness] = useState(() => {
        const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
        return storedBusiness ? JSON.parse(storedBusiness) : null;
      });

      const handleRemove = async () => {
        setIsRemoving(true);
        try {
          const response = await UserService.removeUserFromBusiness(selectedBusiness.id, row.original.publicId);
          if (response.data.result === "success") {
            showToastWithTimeout("success", "✅ User Removed", response.data.message, 3000);
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            showToastWithTimeout("error", "Error", response.data.message, 3000);
          }
        } catch (error) {
          console.log(error);
          showToastWithTimeout("error", "Error", "Failed to remove user from business.", 3000);
        } finally {
          setIsRemoving(false);
        }
      };

      const handleDelete = async () => {
        setIsDeleting(true);
        try {
          const response = await UserService.deleteUser(selectedBusiness.id, row.original.publicId);
          if (response.data.result === "success") {
            showToastWithTimeout("success", "User deleted", `${row.original.firstName} ${row.original.lastName} has been deleted successfully.`, 3000);
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            showToastWithTimeout("error", "Deletion failed", response.data.message, 3000);
          }
        } catch (error) {
          console.log(error);
          showToastWithTimeout("error", "Error", "An error occurred while deleting the user", 3000);
        } finally {
          setIsDeleting(false);
        }
      };

      return (
        <>
          <FormModal
            open={isModalOpen}
            setOpen={setModalOpen}
            title="Update User"
          >
            <EditUserForm user={row.original} />
          </FormModal>
          <FormModal
            open={isRemoveModalOpen}
            setOpen={setRemoveModalOpen}
            title="Remove User"
          >
            <div className="space-y-4">
              <p className="text-gray-700">Are you sure you want to remove <span className="font-semibold text-gray-900">{row.original.firstName} {row.original.lastName}</span> from <span className="font-semibold text-gray-900">{selectedBusiness?.name}</span>?</p>
              <p className="text-sm text-gray-500">This action cannot be undone.</p>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setRemoveModalOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleRemove} className="bg-red-600 hover:bg-red-700 text-white">
                  {isRemoving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Remove User
                </Button>
              </div>
            </div>
          </FormModal>
          <FormModal
            open={isDeleteModalOpen}
            setOpen={setDeleteModalOpen}
            title="Delete User"
          >
            <div className="space-y-4 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full bg-destructive/10">
                  <Trash2 className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-medium">Confirm Deletion</h3>
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to delete {row.original.firstName} {row.original.lastName}?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete User
                </Button>
              </div>
            </div>
          </FormModal>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-slate-100 rounded-full">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[180px] shadow-lg">
                      <DropdownMenuLabel className="text-xs text-gray-500 font-normal">User Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => setModalOpen(true)} className="cursor-pointer hover:bg-slate-50">
                        <Edit2 className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="text-sm">Edit User</span>
                      </DropdownMenuItem>
                      {row.original.isAdmin && (
                        <DropdownMenuItem onSelect={() => setRemoveModalOpen(true)} className="cursor-pointer hover:bg-slate-50">
                          <Trash2 className="w-4 h-4 mr-2 text-red-600" />
                          <span className="text-sm">Remove User</span>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onSelect={() => setDeleteModalOpen(true)} className="cursor-pointer hover:bg-slate-50 text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" />
                        <span className="text-sm">Delete User</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Manage User</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      );
    },
  },
  {
    accessorKey: "publicId",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        User Profile
        <ArrowUpDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
      </Button>
    ),
    cell: ({ row }) => (
      <Button
        onClick={() => window.location.href = `/user/${row.original.publicId}`}
        variant="outline"
        size="sm"
        className="h-8 px-3 text-xs font-medium text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 transition-colors"
      >
        <UserCircle className="w-3.5 h-3.5 mr-1.5" />
        View Profile
      </Button>
    ),
  },
  {
    accessorKey: "username",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        Username
        <ArrowUpDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
      </Button>
    ),
    cell: ({ row }) => {
      const initials = `${row.original.firstName.charAt(0)}${row.original.lastName.charAt(0)}`.toUpperCase();
      return (
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8 bg-blue-100 text-blue-700">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium text-sm text-gray-900">{row.original.username}</span>
            <span className="text-xs text-gray-500">{row.original.firstName} {row.original.lastName}</span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "firstName",
    header: "First Name",
    cell: ({ row }) => (
      <span className="text-sm text-gray-700">{row.original.firstName}</span>
    ),
  },
  {
    accessorKey: "lastName",
    header: "Last Name",
    cell: ({ row }) => (
      <span className="text-sm text-gray-700">{row.original.lastName}</span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status;
      return (
        <Badge className={`px-2 py-0.5 text-xs font-medium ${
          status === "active" 
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
            : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          {status === "active" ? "Active" : "Inactive"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "phoneNumber",
    header: ({ column }) => (
      <div className="flex items-center">
        <Phone className="w-4 h-4 mr-2 text-gray-500" />
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Phone
          <ArrowUpDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
        </Button>
      </div>
    ),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <Phone className="w-3.5 h-3.5 mr-2 text-gray-400" />
              <span className="text-sm text-gray-700">{row.original.phoneNumber}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Call {row.original.firstName}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: "emailAddress",
    header: ({ column }) => (
      <div className="flex items-center">
        <Mail className="w-4 h-4 mr-2 text-gray-500" />
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Email
          <ArrowUpDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
        </Button>
      </div>
    ),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <Mail className="w-3.5 h-3.5 mr-2 text-gray-400" />
              <span className="text-sm text-gray-700">{row.original.emailAddress}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Email {row.original.firstName}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: "physicalAddress",
    header: ({ column }) => (
      <div className="flex items-center">
        <MapPin className="w-4 h-4 mr-2 text-gray-500" />
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Address
          <ArrowUpDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
        </Button>
      </div>
    ),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center max-w-[200px]">
              <MapPin className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-700 truncate">{row.original.physicalAddress}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{row.original.physicalAddress}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: "city",
    header: ({ column }) => (
      <div className="flex items-center">
        <Building className="w-4 h-4 mr-2 text-gray-500" />
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          City
          <ArrowUpDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
        </Button>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex items-center">
        <Building className="w-3.5 h-3.5 mr-2 text-gray-400" />
        <span className="text-sm text-gray-700">{row.original.city}</span>
      </div>
    ),
  },
  {
    accessorKey: "country",
    header: ({ column }) => (
      <div className="flex items-center">
        <Globe className="w-4 h-4 mr-2 text-gray-500" />
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Country
          <ArrowUpDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
        </Button>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex items-center">
        <Globe className="w-3.5 h-3.5 mr-2 text-gray-400" />
        <span className="text-sm text-gray-700">{row.original.country}</span>
      </div>
    ),
  },
  {
    accessorKey: "dateCreated",
    header: ({ column }) => (
      <div className="flex items-center">
        <Clock className="w-4 h-4 mr-2 text-gray-500" />
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Created
          <ArrowUpDown className="w-3.5 h-3.5 ml-1.5 text-gray-500" />
        </Button>
      </div>
    ),
    cell: ({ row }) => {
      // Format date for better display
      const date = row.original.dateCreated;
      
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center">
                <Clock className="w-3.5 h-3.5 mr-2 text-gray-400" />
                <span className="text-sm text-gray-500">{date}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Created on {row.original.dateCreated}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
  }
]
